import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { AuthContext } from '../AuthContext';
import Cover from '../media/TripleFreezerCover2.jpg';
import { FaPlay, FaTimes, FaUser, FaUserPlus, FaGripLines } from 'react-icons/fa';
import { warmupSounds } from '../Utils/soundUtil';

interface Props {
    onStartGame: (playerList: { id: string; name: string }[], matchCount?: number, gameOptions?: { bot?: boolean; botDifficulty?: 'easy' | 'medium' | 'hard'; tournamentContext?: import('../types').TournamentMatchContext }) => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  background: url(${Cover}) no-repeat center center;
  background-size: cover;
  position: relative;
  
  /* Prevent image saving and text selection on mobile */
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
`;

// Header bar with correct color (matching Home.tsx)
const Header = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: rgb(0, 17, 26);
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0 20px;
  z-index: 10;
`;

const HeaderTitle = styled.h1`
  font-family: 'Fjalla One', sans-serif;
  font-size: 16px;
  color: rgb(255, 255, 255);
  margin: 0;
  text-transform: uppercase;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 60px; /* Space for header */
  padding: ${({ theme }) => theme.spacing(2)};
  width: 90%;
  max-width: 400px;
  text-align: center;
  margin-bottom: 60px; /* Space for footer */
`;

// Page title section
const PageTitleSection = styled.div`
  width: 320px;
  height: 60px;
  border-radius: 10px;
  background: rgba(0, 43, 64, 0.5);
  padding: 20px;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const PageTitle = styled.h2`
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 23px;
  color: rgb(255, 255, 255);
  margin: 0;
  text-transform: uppercase;
  text-align: center;
`;

// Main game setup container (matching Home.tsx style)
const GameSetupContainer = styled.div`
  width: 320px;
  border-radius: 10px;
  background: rgba(0, 43, 64, 0.5);
  padding: 20px;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  position: relative;
`;

// Input field - wider to match the image
const Input = styled.input`
  width: 280px; /* Match button width */
  height: 45px; /* Slightly taller */
  margin: 0 auto 15px auto; /* Center the input */
  box-sizing: border-box;
  border: 1px solid rgb(128, 128, 128);
  border-radius: 5px;
  background: rgb(255, 255, 255);
  color: rgb(128, 128, 128);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  text-align: left;
  padding: 10px 15px;
  display: block;

  &::placeholder {
    color: rgb(128, 128, 128);
  }

  &:focus {
    outline: none;
    border-color: rgb(0, 127, 255);
  }
  
  &:disabled {
    background-color: rgb(240, 240, 240);
    color: rgb(160, 160, 160);
    cursor: not-allowed;
  }
`;

// Button styled to match the image - wider and centered
const Button = styled.button`
  width: 280px; /* Full width within container */
  height: 50px; /* Slightly taller */
  border-radius: 5px;
  background: linear-gradient(180deg, rgb(0, 127, 255), rgb(0, 32, 64) 100%);
  color: rgb(255, 255, 255);
  border: none;
  cursor: pointer;
  margin: 0 auto 15px auto; /* Center the button */
  font-family: 'Fjalla One', sans-serif;
  font-size: 22px; /* Bigger font size */
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  /* Icon positioning */
  svg {
    position: absolute;
    left: 15px;
    font-size: 24px; /* Bigger icon size */
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 127, 255, 0.4);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 10px rgba(0, 127, 255, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    
    &:hover {
      transform: none;
      box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.5);
    }
  }
`;

// Options section
const OptionsSection = styled.div`
  width: 280px; /* Match other elements */
  margin: 0 auto 20px auto; /* Center the section */
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: rgb(255, 255, 255);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  font-weight: 400;
`;

const Dropdown = styled.select`
  width: 60px;
  height: 35px; /* Slightly taller to match other inputs */
  box-sizing: border-box;
  border: 1px solid rgb(128, 128, 128);
  border-radius: 5px;
  background: rgb(255, 255, 255);
  color: rgb(13, 13, 13);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  font-weight: 400;
  text-align: center;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: rgb(0, 127, 255);
  }
`;

// Player List
const PlayerListContainer = styled.div`
  width: 100%;
  margin-bottom: 20px;
  /* Prevent text selection and image saving on touch devices */
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
`;

const PlayerItem = styled.div<{ $isDragging?: boolean; $touchActive?: boolean }>`
  width: 280px; /* Match button and input width */
  display: flex;
  align-items: center;
  padding: 12px 15px;
  margin: 0 auto 8px auto; /* Center the player items */
  border-top: 1px solid rgb(128, 128, 128);
  border-bottom: 1px solid rgb(128, 128, 128);
  background: ${props => {
    if (props.$isDragging) return 'rgba(0, 127, 255, 0.4)';
    if (props.$touchActive) return 'rgba(0, 127, 255, 0.2)';
    return 'rgba(255, 255, 255, 0.1)';
  }};
  cursor: grab;
  height: 50px; /* Match button height */
  box-sizing: border-box;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${props => {
    if (props.$isDragging) return 'scale(1.05) translateY(-2px)';
    if (props.$touchActive) return 'scale(1.02) translateY(-1px)';
    return 'scale(1)';
  }};
  box-shadow: ${props => {
    if (props.$isDragging) return '0 8px 25px rgba(0, 127, 255, 0.3), 0 0 0 2px rgba(0, 127, 255, 0.5)';
    if (props.$touchActive) return '0 4px 15px rgba(0, 127, 255, 0.2), 0 0 0 1px rgba(0, 127, 255, 0.3)';
    return '0 2px 4px rgba(0, 0, 0, 0.1)';
  }};
  z-index: ${props => props.$isDragging ? '10' : props.$touchActive ? '5' : '1'};
  border-radius: ${props => props.$isDragging || props.$touchActive ? '8px' : '4px'};
  
  /* Prevent text selection and touch behaviors */
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  
  /* Control touch actions */
  touch-action: pan-y;
  
  /* Add subtle pulse animation when dragging */
  ${props => props.$isDragging && `
    animation: dragPulse 1.5s ease-in-out infinite;
  `}
  
  &:active {
    cursor: grabbing;
  }
  
  /* Hover effect for desktop */
  &:hover:not([data-dragging="true"]) {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  @keyframes dragPulse {
    0%, 100% {
      box-shadow: 0 8px 25px rgba(0, 127, 255, 0.3), 0 0 0 2px rgba(0, 127, 255, 0.5);
    }
    50% {
      box-shadow: 0 12px 35px rgba(0, 127, 255, 0.4), 0 0 0 3px rgba(0, 127, 255, 0.7);
    }
  }
`;

const PlayerIcon = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(255, 255, 255);
  margin-right: 10px;
`;

const GripIcon = styled.div<{ $isDragging?: boolean; $touchActive?: boolean }>`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => {
    if (props.$isDragging) return 'rgb(0, 127, 255)';
    if (props.$touchActive) return 'rgb(100, 180, 255)';
    return 'rgb(255, 255, 255)';
  }};
  margin-right: 10px;
  cursor: grab;
  transition: all 0.2s ease;
  transform: ${props => {
    if (props.$isDragging) return 'scale(1.2)';
    if (props.$touchActive) return 'scale(1.1)';
    return 'scale(1)';
  }};
  opacity: ${props => props.$isDragging || props.$touchActive ? '1' : '0.7'};
  
  /* Add subtle glow when dragging */
  ${props => props.$isDragging && `
    filter: drop-shadow(0 0 8px rgba(0, 127, 255, 0.6));
  `}
  
  &:active {
    cursor: grabbing;
  }
  
  /* Hover effect for desktop */
  &:hover {
    opacity: 1;
    transform: scale(1.1);
    color: rgb(100, 180, 255);
  }
`;

const PlayerName = styled.span`
  color: rgb(255, 255, 255);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  font-weight: 400;
  flex: 1;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: rgb(255, 64, 0);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  padding: 8px; /* Larger touch target */
  border-radius: 4px;
  transition: all 0.2s ease;
  min-width: 32px; /* Ensure minimum touch target size */
  min-height: 32px;
  
  /* Improve touch interaction */
  -webkit-tap-highlight-color: rgba(255, 64, 0, 0.2);
  touch-action: manipulation; /* Enable fast taps */
  
  &:hover {
    color: rgb(255, 100, 50);
    background: rgba(255, 64, 0, 0.1);
  }
  
  &:active {
    color: rgb(255, 150, 100);
    background: rgba(255, 64, 0, 0.2);
    transform: scale(0.95);
  }
`;

// Bot options styling
const BotOptionsContainer = styled.div`
  width: 280px; /* Match other elements */
  margin: 0 auto 15px auto; /* Center the container */
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 15px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  box-sizing: border-box;
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgb(255, 255, 255);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  cursor: pointer;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
`;

const BotDifficultyContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: 24px;
`;

const BotDifficultySelect = styled.select`
  padding: 5px 10px;
  font-size: 14px;
  border-radius: 3px;
  border: 1px solid rgb(128, 128, 128);
  background-color: rgb(255, 255, 255);
  color: rgb(13, 13, 13);
  font-family: 'Roboto Condensed', sans-serif;
`;

const StartScreen: React.FC<Props> = ({ onStartGame }) => {
  const [playerName, setPlayerName] = useState('');
  const [playerList, setPlayerList] = useState<{ id: string; name: string }[]>([]);
  const { isAuthenticated, user } = useContext(AuthContext);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [touchStartTime, setTouchStartTime] = useState<number | null>(null);
  const [touchActiveIndex, setTouchActiveIndex] = useState<number | null>(null);
  const [matchCount, setMatchCount] = useState<number>(1);
  // Bot game state
  const [playVsBot, setPlayVsBot] = useState(false);
  const [botDifficulty, setBotDifficulty] = useState<'easy' | 'medium' | 'hard'>('easy');
  
  // Ref for the player list container to handle touch events properly
  const playerListRef = useRef<HTMLDivElement>(null);
  
  // Constants for touch gesture detection
  const DRAG_THRESHOLD = 15; // pixels to start drag
  const LONG_PRESS_DURATION = 150; // ms for long press detection

  // Game sounds are deferred from app boot — start fetching them now, in the
  // lobby, so everything is cached by the time the first dice hit the table.
  useEffect(() => {
    warmupSounds();
  }, []);

  useEffect(() => {
    // Initialize player list with authenticated user when not in bot mode
    if (isAuthenticated && user && !playVsBot) {
      setPlayerList((prevList) => {
        const isHostInList = prevList.some((player) => player.id === user.uid);
        if (!isHostInList) {
          return [{ id: user.uid, name: user.name }, ...prevList];
        }
        return prevList;
      });
    }
    
    // If switching to bot mode, set player list to user + bot
    if (isAuthenticated && user && playVsBot) {
      setPlayerList([
        { id: user.uid, name: user.name },
        { id: 'bot', name: 'BOT' },
      ]);
    }
    
    // If switching off bot mode, clear bot from player list and ensure user is added
    if (!playVsBot && isAuthenticated && user) {
      setPlayerList((prevList) => {
        const filteredList = prevList.filter((p) => p.id !== 'bot');
        const isHostInList = filteredList.some((player) => player.id === user.uid);
        if (!isHostInList) {
          return [{ id: user.uid, name: user.name }, ...filteredList];
        }
        return filteredList;
      });
    }
  }, [isAuthenticated, user, playVsBot]);

  // Separate useEffect to handle initial user addition
  useEffect(() => {
    if (isAuthenticated && user && !playVsBot && playerList.length === 0) {
      setPlayerList([{ id: user.uid, name: user.name }]);
    }
  }, [isAuthenticated, user, playVsBot, playerList.length]);

  const addPlayer = () => {
    if (playerName.trim() !== '' && !playVsBot) {
      setPlayerList((prevList) => [
        ...prevList,
        { id: Date.now().toString(), name: playerName.trim() },
      ]);
      setPlayerName('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      addPlayer();
    }
  };

  const handleStartGame = () => {
    if (playVsBot) {
      onStartGame(playerList, matchCount, { bot: true, botDifficulty });
    } else {
      onStartGame(playerList, matchCount);
    }
  };

  // Improved touch event handlers for mobile drag and drop
  const handleTouchStart = (e: React.TouchEvent, index: number) => {
    if (playVsBot) return;
    
    // Check if touch is on remove button - if so, don't start drag
    const target = e.target as Element;
    const isRemoveButton = target.closest('[data-remove-button]');
    
    if (isRemoveButton) {
      // Allow the remove button to handle the touch event normally
      return;
    }
    
    // Prevent default touch behaviors for drag area only
    e.preventDefault();
    
    const touch = e.touches[0];
    setTouchStartY(touch.clientY);
    setTouchStartX(touch.clientX);
    setTouchStartTime(Date.now());
    setDraggedIndex(index);
    setTouchActiveIndex(index); // Immediate feedback
    setIsDragging(false);
  };

  const handleTouchEnd = () => {
    setDraggedIndex(null);
    setTouchStartY(null);
    setTouchStartX(null);
    setTouchStartTime(null);
    setTouchActiveIndex(null); // Clear immediate feedback
    setIsDragging(false);
  };

  // Enhanced touch move handler with better gesture detection
  const handleTouchMoveNative = useCallback((e: TouchEvent) => {
    if (playVsBot || draggedIndex === null || touchStartY === null || touchStartX === null || touchStartTime === null) {
      return;
    }
    
    // Always prevent default to stop scrolling/zooming/text selection
    e.preventDefault();
    e.stopPropagation();
    
    const touch = e.touches[0];
    const currentY = touch.clientY;
    const currentX = touch.clientX;
    const diffY = Math.abs(currentY - touchStartY);
    const diffX = Math.abs(currentX - touchStartX);
    const timeDiff = Date.now() - touchStartTime;
    
    // Enhanced drag detection: require minimum movement and time
    if (!isDragging) {
      const shouldStartDrag = (
        (diffY > DRAG_THRESHOLD && diffY > diffX) || // Primarily vertical movement
        (timeDiff > LONG_PRESS_DURATION && (diffY > 5 || diffX > 5)) // Long press with slight movement
      );
      
      if (shouldStartDrag) {
        setIsDragging(true);
        // Add haptic feedback if available
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
      }
      return;
    }

    // Only proceed with reordering if we're actively dragging
    if (isDragging) {
      // Find which player item the touch is currently over
      const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
      
      if (elementBelow) {
        // Find the player item container
        const playerItem = elementBelow.closest('[data-player-index]');
        if (playerItem) {
          const targetIndex = parseInt(playerItem.getAttribute('data-player-index') || '0');
          
          if (draggedIndex !== targetIndex && targetIndex >= 0 && targetIndex < playerList.length) {
            setPlayerList(prevList => {
              const updatedList = [...prevList];
              const draggedPlayer = updatedList[draggedIndex];
              updatedList.splice(draggedIndex, 1);
              updatedList.splice(targetIndex, 0, draggedPlayer);
              return updatedList;
            });
            setDraggedIndex(targetIndex);
            
            // Add subtle haptic feedback for reorder
            if (navigator.vibrate) {
              navigator.vibrate(25);
            }
          }
        }
      }
    }
  }, [playVsBot, draggedIndex, touchStartY, touchStartX, touchStartTime, isDragging, DRAG_THRESHOLD, LONG_PRESS_DURATION, playerList.length]);

  // Set up native touch event listeners to avoid passive event warnings
  useEffect(() => {
    const playerListElement = playerListRef.current;
    if (!playerListElement) return;

    // Add touch event listeners with passive: false to allow preventDefault
    playerListElement.addEventListener('touchmove', handleTouchMoveNative, { passive: false });
    playerListElement.addEventListener('touchstart', (e) => {
      // Prevent context menu and text selection on touch start, but exclude remove button
      const target = e.target as Element;
      const isPlayerItem = target.closest('[data-player-index]');
      const isRemoveButton = target.closest('[data-remove-button]');
      
      if (isPlayerItem && !isRemoveButton) {
        e.preventDefault();
      }
    }, { passive: false });

    return () => {
      playerListElement.removeEventListener('touchmove', handleTouchMoveNative);
      // Note: For cleanup, we can't remove the exact same function reference since it's inline
      // This is acceptable since the component will unmount and the element will be removed
    };
  }, [handleTouchMoveNative]);



  return (
    <Container>
      {/* Header */}
      <Header>
        <HeaderTitle>TRIPLE FREEZER</HeaderTitle>
      </Header>

      <ContentWrapper>
        {/* Page Title Section */}
        <PageTitleSection>
          <PageTitle>GAME LOBBY</PageTitle>
        </PageTitleSection>
        
        {/* Game Setup Container */}
        <GameSetupContainer>
          {/* Input Field */}
          <Input
            type="text"
            placeholder={playVsBot ? "Play vs Bot enabled" : "Enter player name"}
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={playVsBot}
          />
          
          {/* Add Player Button */}
          <Button onClick={addPlayer} disabled={playVsBot}>
            <FaUserPlus />
            ADD PLAYER
          </Button>
          
          {/* Bot Options */}
          {isAuthenticated && user && (
            <BotOptionsContainer>
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  checked={playVsBot}
                  onChange={(e) => setPlayVsBot(e.target.checked)}
                />
                Play vs Bot
              </CheckboxContainer>
              
              {playVsBot && (
                <BotDifficultyContainer>
                  <span style={{ 
                    color: 'rgb(255, 255, 255)', 
                    fontFamily: 'Roboto Condensed, sans-serif',
                    fontSize: '14px'
                  }}>
                    Difficulty:
                  </span>
                  <BotDifficultySelect
                    value={botDifficulty}
                    onChange={(e) => setBotDifficulty(e.target.value as 'easy' | 'medium' | 'hard')}
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </BotDifficultySelect>
                </BotDifficultyContainer>
              )}
            </BotOptionsContainer>
          )}
          
          {!isAuthenticated && (
            <div style={{ 
              width: '280px',
              margin: '0 auto 15px auto',
              color: 'rgb(255, 255, 255)', 
              fontFamily: 'Roboto Condensed, sans-serif',
              fontSize: '14px',
              textAlign: 'center',
              padding: '15px',
              background: 'rgba(255, 0, 0, 0.1)',
              borderRadius: '5px',
              boxSizing: 'border-box'
            }}>
              Log in to play vs Bot
            </div>
          )}
          
          {/* Options Section */}
          <OptionsSection>
            <OptionRow>
              <span>Number of matches:</span>
              <Dropdown
                value={matchCount}
                onChange={(e) => setMatchCount(parseInt(e.target.value))}
              >
                {[1, 3, 5, 7, 9].map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </Dropdown>
            </OptionRow>
          </OptionsSection>
          
          {/* Player List */}
          <PlayerListContainer ref={playerListRef}>
            {playerList.map((player, index) => (
              <PlayerItem
                key={player.id}
                data-player-index={index}
                data-dragging={draggedIndex === index && isDragging}
                draggable={!playVsBot}
                $isDragging={draggedIndex === index && isDragging}
                $touchActive={touchActiveIndex === index && !isDragging}
                onDragStart={() => !playVsBot && setDraggedIndex(index)}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (!playVsBot && draggedIndex !== null && draggedIndex !== index) {
                    const updatedList = [...playerList];
                    const draggedPlayer = updatedList[draggedIndex!];
                    updatedList.splice(draggedIndex!, 1);
                    updatedList.splice(index, 0, draggedPlayer);
                    setPlayerList(updatedList);
                    setDraggedIndex(index);
                  }
                }}
                onDragEnd={() => !playVsBot && setDraggedIndex(null)}
                onTouchStart={(e) => handleTouchStart(e, index)}
                onTouchEnd={handleTouchEnd}
              >
                <PlayerIcon>
                  <FaUser />
                </PlayerIcon>
                <GripIcon 
                  $isDragging={draggedIndex === index && isDragging}
                  $touchActive={touchActiveIndex === index && !isDragging}
                >
                  <FaGripLines />
                </GripIcon>
                <PlayerName>
                  {player.name} {player.id === user?.uid && ' (Host)'}
                </PlayerName>
                <RemoveButton 
                  data-remove-button
                  onClick={() => setPlayerList(playerList.filter((_, i) => i !== index))}
                  onTouchStart={(e) => e.stopPropagation()} // Prevent drag from interfering
                  onTouchEnd={(e) => e.stopPropagation()} // Prevent drag from interfering
                  style={{ display: playVsBot && player.id === 'bot' ? 'none' : 'flex' }}
                >
                  <FaTimes />
                </RemoveButton>
              </PlayerItem>
            ))}
          </PlayerListContainer>
          
          {/* Start Game Button */}
          <Button
            onClick={handleStartGame}
            disabled={playerList.length === 0 && !(isAuthenticated && user)}
          >
            <FaPlay />
            START GAME
          </Button>
        </GameSetupContainer>
      </ContentWrapper>
    </Container>
  );
};

export default StartScreen;
